import { useEffect, useState } from "react";
import { PlugsConnected, CheckCircle, XCircle, CreditCard, EnvelopeSimple, WhatsappLogo, Sparkle, Cloud, FileText, PenNib } from "@phosphor-icons/react";
import api from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

const LIVE = [
  { k: "razorpay", icon: CreditCard, name: "Razorpay Payments", note: "Generates online pay links on invoices and marks them paid automatically." },
  { k: "razorpay_webhook", icon: CreditCard, name: "Razorpay Webhook", note: "Confirms payments the moment they clear." },
  { k: "email", icon: EnvelopeSimple, name: "Email (Resend)", note: "Invoices, digests, assignment alerts and password resets." },
  { k: "whatsapp", icon: WhatsappLogo, name: "WhatsApp (Twilio)", note: "Instant alerts for new requests and staff reminders. Needs your Twilio SID, auth token and sender number." },
  { k: "ai", icon: Sparkle, name: "AI Drafting", note: "Writes outreach emails and campaign copy for you." },
  { k: "object_storage", icon: Cloud, name: "Document Storage", note: "Stores job documents and site photos securely." },
];

const PLANNED = [
  { icon: FileText, name: "Accounting export (Tally)", note: "Push invoices into Tally — say the word and we'll wire it up." },
  { icon: PenNib, name: "E-signature provider", note: "Digitally sign valuation reports before delivery." },
];

export default function Integrations() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/integrations/status").then((r) => setStatus(r.data)).catch(() => setStatus({})); }, []);

  if (!status) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Checking integrations…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="integrations-page">
      <PageHeader overline="My Account" title="Integrations" subtitle="What the Hub is connected to, and what's waiting on credentials" />

      <Card className="p-6 border-border">
        <SectionTitle icon={PlugsConnected}>Connected services</SectionTitle>
        <div className="space-y-2">
          {LIVE.map((s) => {
            const on = !!status[s.k];
            return (
              <div key={s.k} className="flex items-start gap-3 p-4 rounded-md border border-border bg-white" data-testid={`integration-${s.k}`}>
                <s.icon size={22} weight="duotone" className={on ? "text-emerald-600 shrink-0" : "text-muted-foreground shrink-0"} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-primary">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.note}</div>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border font-semibold uppercase tracking-wider ${on ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {on ? <CheckCircle size={11} weight="fill" /> : <XCircle size={11} />} {on ? "Connected" : "Not set up"}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle>Available on request</SectionTitle>
        <div className="space-y-2">
          {PLANNED.map((s) => (
            <div key={s.name} className="flex items-start gap-3 p-4 rounded-md border border-dashed border-border bg-muted/40" data-testid={`planned-${s.name.replace(/\W+/g, "-")}`}>
              <s.icon size={22} weight="duotone" className="text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-primary">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.note}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {!status.whatsapp && (
        <Card className="p-5 border-border bg-primary text-white">
          <div className="flex items-start gap-3">
            <WhatsappLogo size={22} weight="fill" className="text-accent shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-semibold">Turn on WhatsApp alerts</div>
              <p className="text-sm text-white/70 mt-1">Send us your Twilio Account SID, Auth Token, WhatsApp sender number and your own WhatsApp number, and new-request alerts plus staff reminders start going out instantly. Until then everything falls back to email.</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
